import { useMemo } from 'react';
import { supabaseCoreClient } from '../supabase.ts';
import { useQuery } from '@supabase-cache-helpers/postgrest-react-query';
import { commonQueryProps } from './cache.js';
import type {
  ReportInfo,
  ReportField,
  ReportParameter,
  ReportRoute,
  ProcessedReportInfo,
  ReportMetadata,
} from '../../types/reporting.js';
import { persistedRelations, useMetadataBootReady } from '../persistentMetadataCache.js';

// Queries

export const buildQueryForReportInfo = () =>
  (supabaseCoreClient as any).from('report_info').select('*');

export const buildQueryForReportFields = () =>
  (supabaseCoreClient as any).from('report_field').select('*').order('ordinal_position');

export const buildQueryForReportParameters = () =>
  (supabaseCoreClient as any).from('report_parameter').select('*').order('ordinal_position');

export const buildQueryForReportRoutes = () =>
  (supabaseCoreClient as any).from('report_route').select('*');

// Post-processing

export function postProcessReportMetadata(
  reports: ReportInfo[],
  fields: ReportField[],
  parameters: ReportParameter[],
  routes: ReportRoute[],
): ReportMetadata {
  const fieldsByReport: Record<number, ReportField[]> = {};
  fields.forEach((f) => {
    if (!fieldsByReport[f.report_info]) fieldsByReport[f.report_info] = [];
    fieldsByReport[f.report_info].push(f);
  });

  const paramsByReport: Record<number, ReportParameter[]> = {};
  parameters.forEach((p) => {
    if (!paramsByReport[p.report_info]) paramsByReport[p.report_info] = [];
    paramsByReport[p.report_info].push(p);
  });

  const routeByReport: Record<number, string> = {};
  routes.forEach((r) => {
    routeByReport[r.report_info] = r.route;
  });

  const processed: ProcessedReportInfo[] = reports.map((r) => ({
    ...r,
    fields: fieldsByReport[r.id] ?? [],
    parameters: paramsByReport[r.id] ?? [],
    route: routeByReport[r.id] ?? null,
  }));

  const byKey: Record<string, ProcessedReportInfo> = {};
  const byRoute: Record<string, ProcessedReportInfo> = {};
  processed.forEach((r) => {
    byKey[r.report_key] = r;
    if (r.route) byRoute[r.route] = r;
  });

  return {
    asList: processed,
    byKey,
    byRoute,
    navItems: processed.filter((r) => r.show_in_nav && !r.is_draft),
  };
}

// Hook

interface UseReportMetadataOptions {
  enabled?: boolean;
}

interface UseReportMetadataResult {
  data: ReportMetadata | null;
  error: any;
  isLoading: boolean;
}

export function useReportMetadata({ enabled }: UseReportMetadataOptions): UseReportMetadataResult {
  const bootReady = useMetadataBootReady();
  const { data: reports, error: reportsError, isPending: reportsPending } = useQuery(
    buildQueryForReportInfo(),
    { ...commonQueryProps, enabled: enabled && bootReady, meta: persistedRelations('core_crm.report_info') }
  );
  const { data: fields, error: fieldsError, isPending: fieldsPending } = useQuery(
    buildQueryForReportFields(),
    { ...commonQueryProps, enabled: enabled && bootReady, meta: persistedRelations('core_crm.report_field') }
  );
  const { data: parameters, error: paramsError, isPending: paramsPending } = useQuery(
    buildQueryForReportParameters(),
    { ...commonQueryProps, enabled: enabled && bootReady, meta: persistedRelations('core_crm.report_parameter') }
  );
  const { data: routes, error: routesError, isPending: routesPending } = useQuery(
    buildQueryForReportRoutes(),
    { ...commonQueryProps, enabled: enabled && bootReady, meta: persistedRelations('core_crm.report_route') }
  );

  const isLoading = reportsPending || fieldsPending || paramsPending || routesPending;
  const error = reportsError || fieldsError || paramsError || routesError;

  // Memoized on the source query results so the processed object keeps a stable
  // identity across renders — consumers memoize off it.
  const processedData = useMemo(
    () => (reports && fields && parameters && routes
      ? postProcessReportMetadata(
          reports as unknown as ReportInfo[],
          fields as unknown as ReportField[],
          parameters as unknown as ReportParameter[],
          routes as unknown as ReportRoute[],
        )
      : null),
    [reports, fields, parameters, routes]
  );

  if (isLoading || error || !reports || !fields || !parameters || !routes) {
    return { data: null, error: error ?? null, isLoading };
  }

  return {
    data: processedData,
    error: null,
    isLoading: false,
  };
}
